import React from 'react';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Checkbox } from '@mui/material';
import ViewList from '@mui/icons-material/ViewList';
import { Card, IconButton, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import MultilineEditor from '../../../../../components/common/inputs/MultilineEditor';

export const QuestionnaireGeneralDataCard = ({ questionnaire, onSave, validation, isReadOnly, onPropertyChanged }) => {
  const { t } = useTranslation();
  return (
    <Card
      icon={ViewList}
      iconColor='primary'
      title={t('Questionnaire.GeneralData')}
      actions={
        <IconButton
          color='transparent'
          type='save'
          fontSize='medium'
          tooltip={t('Tooltips.Questionnaire.SaveChanges')}
          onClick={onSave}
          disabled={isReadOnly || !questionnaire.changed || !(validation |> isValid)}
        />
      }
    >
      <Prompt
        when={!isReadOnly && questionnaire.questionnaireId && questionnaire.changed}
        message={t('Questionnaire.LeavingWithoutSavingQuestionnaire')}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            value={questionnaire.name || ''}
            onChange={event => onPropertyChanged('name', event)}
            label={t('Questionnaire.Name')}
            error={!isValid(validation.name)}
            helperText={getErrors(validation.name)}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            fullWidth
            value={questionnaire.code || ''}
            onChange={event => onPropertyChanged('code', event)}
            label={t('Questionnaire.Code')}
            error={!isValid(validation.code)}
            helperText={getErrors(validation.code)}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            isNumeric
            fullWidth
            decimalScale={0}
            value={questionnaire.version || ''}
            onChange={event => {
              if (event !== questionnaire.version) onPropertyChanged('version', event);
            }}
            label={t('Questionnaire.Version')}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DateTime
            name='validFrom'
            value={questionnaire.validFrom}
            timeFormat={false}
            label={t('Questionnaire.ValidFrom')}
            onChange={value => onPropertyChanged('validFrom', value)}
            error={!isValid(validation.validFrom)}
            helperText={getErrors(validation.validFrom)}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DateTime
            name='validTo'
            value={questionnaire.validTo}
            timeFormat={false}
            label={t('Questionnaire.ValidTo')}
            onChange={value => onPropertyChanged('validTo', value)}
            error={!isValid(validation.validTo)}
            helperText={getErrors(validation.validTo)}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={2} lg={1}>
          <FormControlLabel
            control={<Checkbox checked={questionnaire.defaultYN} />}
            label={t('Questionnaire.DefaultYN')}
            onChange={event => onPropertyChanged('defaultYN', event.target.checked)}
            name='defaultYN'
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={2} lg={1}>
          <FormControlLabel
            control={<Checkbox checked={questionnaire.active} />}
            label={t('Questionnaire.Active')}
            onChange={event => onPropertyChanged('active', event.target.checked)}
            name='active'
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={2} lg={1}>
          <FormControlLabel
            control={<Checkbox checked={questionnaire.isHistory} />}
            label={t('Questionnaire.IsHistory')}
            onChange={event => onPropertyChanged('isHistory', event.target.checked)}
            name='isHistory'
            disabled={true}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            fullWidth
            value={questionnaire.additionalParametersDataSource || ''}
            onChange={event => onPropertyChanged('additionalParametersDataSource', event)}
            label={t('Questionnaire.AdditionalParametersDataSource')}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <MultilineEditor
            formulaLabel={t('Questionnaire.FilterFormula')}
            formulaPropertyName='filterFormula'
            formulaPropertyValue={questionnaire.filterFormula || ''}
            isReadOnly={isReadOnly}
            onPropertyChanged={onPropertyChanged}
            validation={validation.filterFormula}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <div>{t('Questionnaire.FormulaHelperText')}</div>
        </Grid>
      </Grid>
    </Card>
  );
};

QuestionnaireGeneralDataCard.propTypes = {
  questionnaire: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  onPropertyChanged: PropTypes.func.isRequired
};

export default QuestionnaireGeneralDataCard;
